// "Call this number again?" (owner request, PJ 2026-10-20: "It should be just warnings on dial backs saying that, hey,
// you have called this back. Do you still want to dial it? And then you should be able to click yes or no."). Shown when
// Dial (Space / D / the button, the dial pad's Call or Enter) meets a lead whose only failing rules are the soft attempt
// rules (GateResult.confirm), or when prepare refused a stale card for those rules alone. "Yes, call" (Enter or Y) dials
// with redialConfirmed; "No" (Esc or N, or a click outside) closes it and leaves the lead as it was. Its keys are taken
// in the capture phase and stopped (hotkeys.ts confirmKeyAction), so they never reach the dialer's hotkeys or the pad.
// Mounted once in PhoneProvider, so it works from the dialer and from the pad on any page.
// PJ 2026-10-28: a lead with a meeting booked asks the same way, titled "Meeting booked. Call anyway?" (GateConfirm.meeting),
// so a setter can call to confirm the meeting or to follow up.
import { useEffect, useRef } from 'preact/hooks';
import { useExternal } from '../components/ui-extra';
import { confirmKeyAction } from './hotkeys';
import { cancelRedial, confirmRedial, getDialer, subscribeDialer } from './store';

export function RedialConfirm() {
  const s = useExternal(subscribeDialer, getDialer);
  const c = s.redialConfirm;
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!c) return;
    // Focus the dialog itself, not a button: the Space or Enter that opened it can't click one on its way up.
    box.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      const action = confirmKeyAction(e, e.target as HTMLElement | null);
      if (action === null) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (action === 'yes') confirmRedial();
      else if (action === 'no') cancelRedial();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [c]);

  if (!c) return null;
  return (
    <div class="vd-confirm-backdrop" onClick={(e) => { if (e.target === e.currentTarget) cancelRedial(); }}>
      <div ref={box} class="card vd-confirm" role="alertdialog" aria-modal="true" aria-labelledby="vd-confirm-title"
        aria-describedby="vd-confirm-body" tabIndex={-1}>
        <h2 id="vd-confirm-title">{c.meeting ? 'Meeting booked. Call anyway?' : 'Call this number again?'}</h2>
        <p class="muted"><b>{c.business}</b>{c.phone ? ` · ${c.phone}` : ''}</p>
        <p id="vd-confirm-body">{c.prompt}</p>
        {c.reasons.length ? <ul>{c.reasons.map((r) => <li>{r.message}</li>)}</ul> : null}
        <div class="row" style="gap:8px">
          <button type="button" class="btn btn-primary" onClick={(e) => { (e.currentTarget as HTMLElement).blur(); confirmRedial(); }}>
            Yes, call <kbd>Enter</kbd>
          </button>
          <button type="button" class="btn" onClick={(e) => { (e.currentTarget as HTMLElement).blur(); cancelRedial(); }}>
            No <kbd>Esc</kbd>
          </button>
        </div>
      </div>
    </div>
  );
}
